import store from '../../store/store';
import { ActionStatus } from '../../common/constants/action-status';
import { BaseComponent } from '../base-component/base-component';
import { CheckoutStep } from './checkout-step';
import { ShopCart } from '../cart/shop-cart/interfaces/shop-cart.interface';
import { StepId } from './shop-checkout.interface';
import { StoreState } from '../../store/store.interface';
import { check3rdPartyPaymentType } from '../../common/utils/payment-util';
import { clearGlobalMessages } from '../alert/global-message';
import { connect } from 'pwa-helpers';
import { getCart, getCartError, getCartStatus } from '../cart/shop-cart/store/shop-cart.selectors';
import {
  getPaymentDetails,
  updateActiveStep,
  getOrderCreationStatus,
  getOrderCreationError,
  updateCheckoutInfoSubmitted,
  validPaymentDetails,
  getActiveStepId,
  updateIsCheckoutComplete,
  updateOrderCreationApiStatus
} from './shop-checkout';
import { handleApiError } from '../../common/utils/api-utils';
import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { getCheckoutLoader } from '../../common/utils/loader-util';

/**
 * Abstract component for concrete checkout steps component.
 * Has common functionality for checkout steps component.
 */
export class CheckoutStepsBase extends connect(store)(BaseComponent) {
  /**
   * Store code related to the checkout process. If not set it will be the national process.
   */
  @property()
  store?: string;

  /**
   * Map to store the order in which steps are displayed.
   */
  @state()
  protected stepOrderStepIdMap: Record<string, StepId> = {};

  /**
   * ID of the active checkout step
   */
  @state()
  protected activeStepId?: StepId = undefined;

  /**
   * ID of the active checkout step
   */
  @state()
  protected activeStepValue? = 1;

  /**
   * Flag to indicate whether checkout is complete
   */
  @state()
  protected isComplete = false;

  /**
   * Flag to indicate whether checkout steps is loading.
   */
  @state()
  protected isLoading = true;

  /**
   * Flag to indicate shippable order.
   */
  @state()
  protected shippable = false;

  /**
   * Flag to prepopulate checkout steps with data from redux.
   */
  @state()
  protected forceComplete = false;

  /**
   * Flag to prepopulate shipping address checkout step with data from redux.
   */
  @state()
  protected bypassShippingAddress = false;

  /**
   * Flag to prepopulate shipping address, shipping method checkout step with data from redux.
   */
  @state()
  protected bypassShippingMethod = false;

  /**
   * Flag to indicate that checkout steps setup has failed.
   */
  @state()
  protected failedToSetup = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mhecomm-checkout-step-complete', this.onStepCompleteEventHandler);
    this.addEventListener('mhecomm-checkout-step-force-complete', this.onStepForceCompleteEventHandler);
    this.addEventListener('mhecomm-checkout-step-force-complete-failed', this.onStepForceCompleteFailedEventHandler);
    this.addEventListener('mhecomm-checkout-step-edited', this.onEditStepEventHandler);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('mhecomm-checkout-step-complete', this.onStepCompleteEventHandler);
    this.removeEventListener('mhecomm-checkout-step-force-complete', this.onStepForceCompleteEventHandler);
    this.removeEventListener('mhecomm-checkout-step-force-complete-failed', this.onStepForceCompleteFailedEventHandler);
    this.removeEventListener('mhecomm-checkout-step-edited', this.onEditStepEventHandler);
  }

  /**
   * Setup Checkout Steps component
   *
   * @param _changedProperties
   */
  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    this.setCheckoutSteps();
  }

  /**
   * Updates internal variables the first time the component is updated.
   */
  protected setCheckoutSteps() {
    const state = store.getState() as StoreState;
    const cart = getCart(store.getState() as StoreState);

    if (cart) {
      const paymentDetails = getPaymentDetails(state);
      const paymentType = paymentDetails?.paymentType;

      this.setCartCheckoutContext(cart);
      if (
        state.shopCheckoutState.activeStepId === StepId.ShippingAddressStep
        && check3rdPartyPaymentType(paymentType, false)
      ) {
        // This condition is here to prevent it from matching any of the below else if conditions.
      } else if (state.shopCheckoutState.activeStepId === StepId.ShippingMethodStep && state.shopCheckoutState.shippingAddress) {
        this.bypassShippingAddress = true;
      } else if (state.shopCheckoutState.activeStepId === StepId.PaymentBillingAddressStep && state.shopCheckoutState.shippingAddress && state.shopCheckoutState.selectedShippingMethod) {
        this.bypassShippingAddress = true;
        this.bypassShippingMethod = true;
      } else if (state.shopCheckoutState.shippingAddress && state.shopCheckoutState.selectedShippingMethod && validPaymentDetails(state)) {
        this.forceComplete = true;
      } else if (validPaymentDetails(state) && !this.shippable) {
        this.forceComplete = true;
      } else if (
        check3rdPartyPaymentType(paymentType, true)
        && state.shopCheckoutState.shippingAddress && state.shopCheckoutState.billingAddress) {
        this.forceComplete = true;
      }

      this.setStepOrderStepIdMap();
    }
  }

  /**
   * Set the order of the checkout step components.
   * To be overridden by child classes.
   */
  setStepOrderStepIdMap() {
    throw new Error("Method not implemented.");
  }

  /**
   * Set checkout page context.
   * To be overridden by child classes.
   *
   * @param _cart
   */
  setCartCheckoutContext(_cart: ShopCart) {
    throw new Error("Method not implemented.");
  }

  /**
   * Updates internal variables every time the state changes.
   *
   * @param state
   */
  updateCheckoutStepsState(state: StoreState) {
    this.isComplete = state.shopCheckoutState.checkoutInfoSubmitted;
    const newStepId = getActiveStepId(state);
    if (this.activeStepId !== newStepId) {
      this.activeStepId = newStepId;
    }

    const activeStep = Object.keys(this.stepOrderStepIdMap).find(key => this.stepOrderStepIdMap[key] === state.shopCheckoutState.activeStepId);
    this.activeStepValue = activeStep ? parseInt(activeStep) : undefined;

    const stepToActivate = this.renderRoot.querySelector(`#${this.activeStepId }`) as CheckoutStep;
    if (stepToActivate) {
      stepToActivate.setActive(true);
    }
  }

  /**
   * Updates internal variables every time the API response states contain failures.
   *
   * @param state
   */
  handleApiFailures(state: StoreState) {
    if (getOrderCreationStatus(state) === ActionStatus.failed) {
      this.isLoading = false;
      const orderCreationError = getOrderCreationError(state);

      store.dispatch(updateOrderCreationApiStatus(ActionStatus.idle));
      handleApiError(orderCreationError);
      return;
    }

    if (getCartStatus(state) === ActionStatus.failed) {
      this.failedToSetup = true;
      this.isLoading = false;
      handleApiError(getCartError(state));
      return;
    }
  }

  /**
   * Render checkout steps and checkout steps complete button.
   */
  renderComp() {
    return html`
    ${this.failedToSetup
      ? nothing
      : html`
        ${this.isLoading
          ? getCheckoutLoader()
          : html`${this.renderCheckoutSteps()}`
        }
      `
    }`;
  }

  /**
   * Render checkout step components.
   * To be overridden by child classes.
   */
  renderCheckoutSteps() {
    throw new Error("Method not implemented.");
  }

  /**
   * Triggered by child checkout step component when it completes.
   * Loads the next step and displays the results of the current step.
   * If there is no next step, it will enable the complete button.
   */
  onStepCompleteEventHandler(e: Event) {
    const event: CustomEvent = e as CustomEvent;
    this.activeStepId = event.detail.id;
    const currentStepElement = this.renderRoot.querySelector(`#${this.activeStepId}`) as CheckoutStep;
    currentStepElement.setActive(false);
    const nextStepOrder = currentStepElement.getOrder() + 1;
    this.activeStepId = this.stepOrderStepIdMap[nextStepOrder];
    const nextStepElement = this.renderRoot.querySelector(`#${this.activeStepId}`) as CheckoutStep;
    if (nextStepElement) {
      store.dispatch(updateActiveStep(this.activeStepId));
      nextStepElement.loadCheckoutStep();
    } else {
      this.completeSteps();
    }
  }

  /**
   * Triggered by child checkout step component when it is force completed.
   * Loads the next step and displays the results of the current step.
   * If there is no next step, it will enable the complete button.
   */
  onStepForceCompleteEventHandler(e: Event) {
    const event: CustomEvent = e as CustomEvent;
    const currentStepId = event.detail.id;
    const currentStepElement = this.renderRoot.querySelector(`#${currentStepId}`) as CheckoutStep;
    const nextStepOrder = currentStepElement.getOrder() + 1;
    const nextStepId = this.stepOrderStepIdMap[nextStepOrder];
    const nextStepElement = this.renderRoot.querySelector(`#${nextStepId}`) as CheckoutStep;
    if (nextStepElement) {
      nextStepElement.loadCheckoutStep();
    } else {
      this.completeSteps();
    }
  }

  /**
   * Triggered by child checkout step component when force complete fails.
   *
   * @param _e
   */
  onStepForceCompleteFailedEventHandler(_e: Event) {
    this.forceComplete = false;
  }

  /**
   * When all steps are completed update checkout steps state.
   */
  protected completeSteps() {
    this.activeStepId = undefined;
    store.dispatch(updateActiveStep(this.activeStepId));
    this.isComplete = true;
    store.dispatch(updateIsCheckoutComplete(this.isComplete));
    store.dispatch(updateCheckoutInfoSubmitted(this.isComplete));
    store.dispatch(clearGlobalMessages());
  }

  /**
   * Triggered by child checkout step component when edit button is clicked on it.
   * Update the UI to show edited step.
   */
  onEditStepEventHandler(e: Event) {
    this.isComplete = false;
    this.forceComplete = false;

    store.dispatch(updateIsCheckoutComplete(this.isComplete));
    store.dispatch(updateCheckoutInfoSubmitted(this.isComplete));

    const event: CustomEvent = e as CustomEvent;
    this.activeStepId = event.detail.id;
    store.dispatch(updateActiveStep(this.activeStepId));
    Object.keys(this.stepOrderStepIdMap).forEach(key => {
      if (this.stepOrderStepIdMap[key] != this.activeStepId) {
        const step = this.renderRoot!.querySelector(`#${this.stepOrderStepIdMap[key]}`) as CheckoutStep;
        step.setActive(false);
      }
    });
    const stepToEditElement = this.renderRoot.querySelector(`#${this.activeStepId}`) as CheckoutStep;
    stepToEditElement.editCheckoutStep()
  }
}
